import { patchQuery } from '../../services/restartReload';
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
    BatchMoveItem,
    BoardActionItemDto,
    BoardCommentDto,
    BoardDragDto,
    BoardDto,
    BoardItemDto,
    BoardPresenceDto,
    BoardStateDto,
    CombineIdeasDto,
    CreateBoardActionItemDto,
    CreateBoardCommentDto,
    CreateBoardDto,
    CreateBoardItemDto,
    ToggleVoteResultDto,
    UpdateBoardActionItemDto,
    UpdateBoardCommentDto,
    UpdateBoardDto,
    UpdateBoardItemDto,
    UpdateBoardVotingDto,
} from '../../../../shared/types/board';
import { api, shouldRetry } from '../../services/api';
import * as cache from './liveCache';
import { platformKeys } from '../../services/platform';

export const boardKeys = {
    all: ['board'] as const,
    board: (shareId: string) => ['board', shareId] as const,
};

const base = (shareId: string) => `/api/boards/${encodeURIComponent(shareId)}`;

/** Update the cached board state, if loaded (restarting a reload that predates the change). */
export function patchBoard(qc: QueryClient, shareId: string, fn: (state: BoardStateDto) => BoardStateDto) {
    patchQuery(qc, boardKeys.board(shareId), fn);
}

/** Direct cache edits for optimistic updates that don't map to one mutation. */
export function useBoardCacheActions(shareId: string) {
    const qc = useQueryClient();
    return useMemo(() => ({
        patchItem: (itemId: string, changes: Partial<BoardItemDto>) => patchBoard(qc, shareId, s => cache.applyItemUpdated(s, { itemId, changes })),
        reload: () => { void qc.invalidateQueries({ queryKey: boardKeys.board(shareId) }); },
    }), [qc, shareId]);
}

export async function createBoard(dto: CreateBoardDto): Promise<BoardDto> {
    return (await api.post<BoardDto>('/api/boards', dto)).data;
}

/** Bring an archived board back (from Home's Archived list). */
export async function restoreBoard(shareId: string): Promise<void> {
    await api.post(`${base(shareId)}/restore`);
}

// ── Board ─────────────────────────────────────────────────────

export function useBoard(shareId: string | undefined) {
    return useQuery({
        queryKey: boardKeys.board(shareId ?? ''),
        queryFn: async () => (await api.get<BoardStateDto>(base(shareId!))).data,
        enabled: !!shareId,
        retry: shouldRetry,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
}

export function useUpdateBoard(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: UpdateBoardDto) => (await api.patch<BoardDto>(base(shareId), dto)).data,
        onSuccess: board => patchBoard(qc, shareId, s => cache.applyBoardUpdated(s, board)),
    });
}

export type BoardStatusAction = 'conclude' | 'reopen' | 'archive' | 'restore';

export function useBoardStatus(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (action: BoardStatusAction) => (await api.post<BoardDto>(`${base(shareId)}/${action}`)).data,
        onSuccess: board => {
            patchBoard(qc, shareId, s => cache.applyBoardUpdated(s, board));
            qc.invalidateQueries({ queryKey: platformKeys.all });
        },
    });
}

export function useDeleteBoard(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => { await api.delete(base(shareId)); },
        onSuccess: () => {
            qc.removeQueries({ queryKey: boardKeys.board(shareId) });
            qc.invalidateQueries({ queryKey: platformKeys.all });
        },
    });
}

export function exportUrl(shareId: string) {
    return `${base(shareId)}/export/text`;
}

// ── Items ─────────────────────────────────────────────────────

export function useAddBoardItem(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: CreateBoardItemDto) => (await api.post<BoardItemDto>(`${base(shareId)}/items`, dto)).data,
        onSuccess: item => patchBoard(qc, shareId, s => cache.applyItemCreated(s, item)),
    });
}

export function useUpdateBoardItem(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ itemId, dto }: { itemId: string; dto: UpdateBoardItemDto }) => {
            await api.patch(`${base(shareId)}/items/${itemId}`, dto);
        },
        // Keep the cache in step with what we sent so the planner doesn't resend it
        onMutate: ({ itemId, dto }) => patchBoard(qc, shareId, s => cache.applyItemUpdated(s, { itemId, changes: dto })),
    });
}

export function useBatchMoveBoardItems(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (moves: BatchMoveItem[]) => { await api.patch(`${base(shareId)}/items/batch`, { moves }); },
        onMutate: moves => patchBoard(qc, shareId, s => cache.applyItemsMoved(s, moves)),
    });
}

export function useDeleteBoardItem(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (itemId: string) => { await api.delete(`${base(shareId)}/items/${itemId}`); },
        onSuccess: (_, itemId) => patchBoard(qc, shareId, s => cache.applyItemDeleted(s, itemId)),
    });
}

// ── Comments ──────────────────────────────────────────────────

export function useAddBoardComment(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: CreateBoardCommentDto) => (await api.post<BoardCommentDto>(`${base(shareId)}/comments`, dto)).data,
        onSuccess: comment => patchBoard(qc, shareId, s => cache.applyCommentAdded(s, comment)),
    });
}

export function useDeleteBoardComment(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (commentId: string) => { await api.delete(`${base(shareId)}/comments/${commentId}`); },
        onSuccess: (_, commentId) => patchBoard(qc, shareId, s => cache.applyCommentsDeleted(s, cache.commentSubtree(s.comments, commentId))),
    });
}

export function useMoveBoardComment(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ commentId, dto }: { commentId: string; dto: UpdateBoardCommentDto }) => {
            await api.patch(`${base(shareId)}/comments/${commentId}`, dto);
        },
        onSuccess: (_, { commentId, dto }) => patchBoard(qc, shareId, s => cache.applyCommentsMoved(s, cache.commentSubtree(s.comments, commentId), dto.x, dto.y)),
    });
}

// ── Votes ─────────────────────────────────────────────────────

export function useToggleBoardVote(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (itemId: string) => (await api.post<ToggleVoteResultDto>(`${base(shareId)}/votes/${itemId}`)).data,
        onSuccess: result => patchBoard(qc, shareId, s => cache.applyVoteChanged(s, result)),
    });
}

/** Count look-alike stickies as one idea (dots move with them); everyone's own dots arrive with the live event. */
export function useCombineIdeas(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: CombineIdeasDto) => (await api.post<BoardDto>(`${base(shareId)}/combine`, dto)).data,
        onSuccess: board => patchBoard(qc, shareId, s => cache.applyBoardUpdated(s, board)),
    });
}

export function useSeparateIdea(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (itemId: string) => (await api.delete<BoardDto>(`${base(shareId)}/combine/${itemId}`)).data,
        onSuccess: board => patchBoard(qc, shareId, s => cache.applyBoardUpdated(s, board)),
    });
}

export function useUpdateBoardVoting(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: UpdateBoardVotingDto) => (await api.patch<BoardDto>(`${base(shareId)}/voting`, dto)).data,
        // Totals arrive with the voting_changed live event; refetch covers a dropped stream
        onSuccess: (_, dto) => {
            if (dto.phase === 'closed' || dto.clearVotes) qc.invalidateQueries({ queryKey: boardKeys.board(shareId) });
        },
    });
}

// ── Action items ──────────────────────────────────────────────

export function useAddBoardActionItem(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: CreateBoardActionItemDto) => (await api.post<BoardActionItemDto>(`${base(shareId)}/action-items`, dto)).data,
        onSuccess: item => patchBoard(qc, shareId, s => cache.applyActionItemChanged(s, item)),
    });
}

export function useUpdateBoardActionItem(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ actionItemId, dto }: { actionItemId: string; dto: UpdateBoardActionItemDto }) =>
            (await api.patch<BoardActionItemDto>(`${base(shareId)}/action-items/${actionItemId}`, dto)).data,
        onSuccess: item => patchBoard(qc, shareId, s => cache.applyActionItemChanged(s, item)),
    });
}

export function useDeleteBoardActionItem(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (actionItemId: string) => { await api.delete(`${base(shareId)}/action-items/${actionItemId}`); },
        onSuccess: (_, actionItemId) => patchBoard(qc, shareId, s => cache.applyActionItemDeleted(s, actionItemId)),
    });
}

// ── Cursor presence ───────────────────────────────────────────

/** Fire-and-forget cursor position. Throttle at the call site. */
export function useSendBoardPresence(shareId: string) {
    /** `drag`: the drag in progress (others see it live), null to end it, omitted for a plain cursor move. */
    return useCallback((x: number, y: number, drag?: BoardDragDto | null) => {
        const body: BoardPresenceDto = { x: Math.round(x), y: Math.round(y) };
        if (drag !== undefined) body.drag = drag;
        return api.patch(`${base(shareId)}/presence`, body).then(() => undefined, () => undefined);
    }, [shareId]);
}
